import { useCallback, useEffect, useState } from 'react';

import { TailRecord } from '../types';
import { fetchTailNumbers } from '../api';
import { errorMessage } from '../errors';

/**
 * Single source of truth for loading the IN_WORK tail record list.
 *
 * The initial load runs when `enabled` becomes true (typically after the
 * user authenticates), and `load` itself is exposed as `refresh` so callers
 * can retrigger the same pipeline. A local boolean cancel flag suppresses
 * state updates from a load whose component unmounted mid-request; the
 * complexity of AbortController was removed because fetchTailNumbers does
 * not plumb the signal into fetch() — the boolean is simpler and honest
 * about what it does (simp-003).
 */
export function useTailNumbers(enabled: boolean): {
  records: TailRecord[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [records, setRecords] = useState<TailRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Module-scoped `cancelled` is reset on each load via the ref-like closure.
  const load = useCallback(async (isCancelled?: () => boolean): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchTailNumbers();
      if (isCancelled?.()) return;
      setRecords(next);
    } catch (err: unknown) {
      if (isCancelled?.()) return;
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [enabled, load]);

  // `load` itself is the refresh callback — exposing it directly avoids the
  // useless one-liner wrapper flagged by simp-005.
  return { records, loading, error, refresh: load };
}
